import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import { InlineAlert, LoadingButton } from "src/grading/components";
import Loader from "src/grading/components/Loader/Loader";
import { StatTile } from "src/grading/components/StatTile/StatTile";
import { PageHeader } from "src/grading/layout/PageHeader";
import { EVENT_LABEL, EventId } from "src/models/sessions";
import {
  useGetClassQuery,
  useGetRosterQuery,
} from "src/store/reducers/classes/classesApiSlice";
import {
  addBreadcrumb,
  clearBreadcrumbs,
} from "src/store/reducers/navigation/navigationSlice";
import {
  ReportRange,
  useGetClassReportQuery,
  useLazyGetClassScorecardExportQuery,
} from "src/store/reducers/reports/reportsApiSlice";
import { ExportsCard, RangePicker, ReportBody } from "./ReportWidgets";

const CATEGORIES = ["core", "cardio", "upper"] as const;
const NATIONAL_OR_BETTER = ["presidential", "national"];

/** Class completion by category, award counts, modifications used, and each student's own change between windows (FR-061). */
const ClassReportPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { classId } = useParams<{ classId: string }>();
  const [range, setRange] = useState<ReportRange>({});
  const {
    data: report,
    isLoading,
    isError,
  } = useGetClassReportQuery({ classId, range });
  const { data: schoolClass } = useGetClassQuery(classId);
  const { data: roster } = useGetRosterQuery(classId);
  const [exportScorecard, { isFetching: exporting }] =
    useLazyGetClassScorecardExportQuery();
  const [error, setError] = useState<string | null>(null);
  const nameOf = (studentId: string) => {
    const s = roster?.find((x) => x.id === studentId);
    return s ? `${s.lastName}, ${s.firstName}` : t("classes.student");
  };
  const className = schoolClass?.name ?? report?.className ?? "";

  useEffect(() => {
    document.title = `${t("reports.classReport", { defaultValue: "Class report" })} · ${t("app.name")}`;
  }, [t]);

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    dispatch(
      addBreadcrumb({
        label: t("reports.title", { defaultValue: "Reports" }),
        path: "/grading/reports",
      }),
    );
    dispatch(addBreadcrumb({ label: className }));
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch, className, t]);

  const accommodations = useMemo(
    () =>
      Object.entries(report?.modificationsUsed ?? {}).map(([key, n]) => {
        const [eventId, level] = key.split(":");
        return {
          label: `${EVENT_LABEL[eventId as EventId] ?? eventId} · ${t(`modification.${level}`)}`,
          count: n,
        };
      }),
    [report, t],
  );

  const download = async () => {
    setError(null);
    try {
      const { data } = await exportScorecard({ classId, range });
      if (data?.url) window.open(data.url, "_blank", "noopener");
    } catch (e) {
      setError(
        (e as { data?: { message?: string } })?.data?.message ??
          t("reports.exportScorecardError", {
            defaultValue: "Could not export the scorecard",
          }),
      );
    }
  };

  if (isLoading) return <Loader />;
  if (isError || !report)
    return (
      <Typography color="error">
        {t("reports.classNotFound", {
          defaultValue:
            "No report for this class yet, or the class is outside your scope.",
        })}
      </Typography>
    );
  const pct = (n: number) =>
    report.tested ? `${Math.round((n / report.tested) * 100)}%` : "—";
  const rate = report.enrolled
    ? Math.round((report.tested / report.enrolled) * 100)
    : 0;
  const bothWindows = report.windowChanges.length > 0;
  const countBetter = (pick: "from" | "to") =>
    report.windowChanges.filter(
      (c) => c[pick].tier && NATIONAL_OR_BETTER.includes(c[pick].tier),
    ).length;
  const sub = [
    schoolClass?.period
      ? `${t("reports.period", { defaultValue: "Period" })} ${schoolClass.period}`
      : null,
    schoolClass?.schoolYear ?? null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box>
      <PageHeader
        title={className}
        description={sub || undefined}
        eyebrow={t("reports.classReport", { defaultValue: "Class report" })}
      />
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <RangePicker range={range} onChange={setRange} />
        {error && <InlineAlert severity="error">{error}</InlineAlert>}
        <ReportBody
          tested={report.tested}
          enrolled={report.enrolled}
          rate={rate}
          tiers={report.tiers}
          groups={[]}
          accommodations={accommodations}
          nationalOrBetter={
            bothWindows
              ? {
                  earlier: countBetter("from"),
                  latest: countBetter("to"),
                  students: report.windowChanges.length,
                }
              : undefined
          }
          extra={
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(3, 1fr)",
                },
              }}
            >
              {CATEGORIES.map((c) => (
                <StatTile
                  key={c}
                  label={`${t(`categories.${c}`)} completed`}
                  value={report.completionByCategory[c]}
                  sub={pct(report.completionByCategory[c])}
                />
              ))}
            </Box>
          }
          exports={
            <ExportsCard>
              <LoadingButton
                variant="outlined"
                loading={exporting}
                onClick={download}
                label={t("reports.exportScorecard", {
                  defaultValue: "Digital Class Scorecard layout (XLSX)",
                })}
                sx={{ borderRadius: `${radius.pill}px` }}
              />
            </ExportsCard>
          }
        />
        <Paper
          sx={{ p: "20px 22px", overflowX: "auto" }}
          component="section"
          aria-label={t("reports.changeBetweenWindows", {
            defaultValue: "Change between windows",
          })}
        >
          <Typography variant="h4" component="h2" gutterBottom>
            {t("reports.changeTitle", {
              defaultValue: "Each student's change between windows",
            })}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("reports.changeBody", {
              defaultValue:
                "Compared only to the student's own earlier result. Students are listed by name.",
            })}
          </Typography>
          <Table
            size="small"
            aria-label={t("reports.changeBetweenWindows", {
              defaultValue: "Change between windows",
            })}
          >
            <TableHead>
              <TableRow>
                <TableCell>{t("classes.student")}</TableCell>
                <TableCell>{t("reports.earlier")}</TableCell>
                <TableCell>{t("reports.latest")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...report.windowChanges]
                .sort((a, b) =>
                  nameOf(a.studentId).localeCompare(nameOf(b.studentId)),
                )
                .map((c) => (
                  <TableRow key={c.studentId}>
                    <TableCell>{nameOf(c.studentId)}</TableCell>
                    <TableCell>
                      {c.from.date} ·{" "}
                      {c.from.tier ? t(`results.tier.${c.from.tier}`) : "—"}
                    </TableCell>
                    <TableCell>
                      {c.to.date} ·{" "}
                      {c.to.tier ? t(`results.tier.${c.to.tier}`) : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              {report.windowChanges.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3}>
                    {t("reports.changeEmpty", {
                      defaultValue:
                        "Appears once a student has results in two windows.",
                    })}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      </Box>
    </Box>
  );
};

export default ClassReportPage;
